import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { ListPagination } from '@/components/list-pagination';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { PersonChip } from '@/platform/components/person-chip';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import {
  formatLeaveRange,
  LEAVE_REQUEST_STATUS_LABEL,
} from '@/products/leave/features/leave-labels';
import { formatLeaveDays } from '@/products/leave/features/preview-vacation-days';
import { useCancelLeaveRequest, useLeaveRequests } from '@/products/leave/features/use-leave';
import { leaveRequestDetailPath } from '@/products/leave/leave-paths';

export function LeaveRequestsPage() {
  usePageTitle('Meine Anträge');
  const [page, setPage] = useState(1);
  const requests = useLeaveRequests('mine', page);
  const cancel = useCancelLeaveRequest();
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <PlatformPageHeader title="Meine Anträge" description="Erholungsurlaub" />
      {requests.isPending ? (
        <p className="text-sm text-muted-foreground">Anträge werden geladen …</p>
      ) : null}
      {requests.isError ? (
        <p className="text-sm text-destructive">
          {requests.error instanceof ApiRequestError
            ? requests.error.message
            : 'Die Anträge konnten nicht geladen werden.'}
        </p>
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {requests.data?.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch keine Anträge.</p>
      ) : null}
      <ul className="space-y-3">
        {requests.data?.items.map((item) => (
          <li key={item.id} className={leavePanelClassName}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <Link to={leaveRequestDetailPath(item.id)} className="font-medium hover:underline">
                {formatLeaveRange(item.startsOn, item.endsOn)} · {formatLeaveDays(item.days)} Tage
              </Link>
              <p className="text-sm text-muted-foreground">
                {LEAVE_REQUEST_STATUS_LABEL[item.status]}
              </p>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{item.absenceType.labelDe}</p>
            {item.substitute ? (
              <p className="mt-1 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  Vertretung:
                  <PersonChip
                    displayName={item.substitute.displayName}
                    profilePhotoUrl={item.substitute.profilePhotoUrl}
                    avatarClassName="size-5"
                  />
                </span>
              </p>
            ) : null}
            {item.status === 'draft' || item.status === 'submitted' ? (
              <Button
                type="button"
                variant="outline"
                className="mt-3"
                disabled={cancel.isPending}
                onClick={() => {
                  setError(null);
                  void cancel.mutateAsync(item.id).catch((caught: unknown) => {
                    setError(
                      caught instanceof ApiRequestError
                        ? caught.message
                        : 'Zurückziehen ist fehlgeschlagen.',
                    );
                  });
                }}
              >
                Zurückziehen
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {requests.data ? (
        <ListPagination
          page={requests.data.page}
          pageSize={requests.data.pageSize}
          total={requests.data.total}
          onPageChange={setPage}
        />
      ) : null}
    </div>
  );
}
